// web/src/help/HelpPage.tsx
//
// Public FAQ at /help. Uses native <details>/<summary> for the accordion
// rows — fully accessible, keyboard-navigable, no JS framework required,
// no extra dependency. Header + footer come from MarketingLayout.
//
// Content principles:
//   - Honest, not marketing fluff.
//   - Concrete (real numbers, real features) where possible.
//   - Concise (2-3 sentences per answer).
//   - Defer formal stuff to legal/contact pages.
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "@/marketing/Seo";

interface QA {
  q: string;
  a: React.ReactNode;
}

interface Section {
  title: string;
  items: QA[];
}

const SECTIONS: Section[] = [
  {
    title: "About Tapelens",
    items: [
      {
        q: "What is Tapelens?",
        a: (
          <>
            A real-time news-analysis tool for active traders. We ingest hundreds
            of financial news feeds, deduplicate stories across wires into
            clusters, and use an LLM to produce a per-instrument directional bias
            every few minutes — with full citations to the source articles you
            can click through to.
          </>
        ),
      },
      {
        q: "Who is it for?",
        a: (
          <>
            Discretionary traders and PMs who need to act on macro and
            single-name news quickly, but don't want to read every wire to spot
            what matters. If your edge depends on being early but informed,
            Tapelens is built for you.
          </>
        ),
      },
      {
        q: "Is this investment advice?",
        a: (
          <>
            No. Tapelens is a news-analysis tool — we surface and summarize
            public information. We don't give investment, legal, or tax advice,
            and we aren't a registered investment adviser. Markets carry risk;
            see our{" "}
            <Link to="/legal/terms" className="underline hover:no-underline">
              Terms
            </Link>{" "}
            for the full disclaimer.
          </>
        ),
      },
      {
        q: "What markets and instruments do you cover?",
        a: (
          <>
            At launch: FX majors, gold (XAUUSD), oil (WTI/BRENT), bitcoin
            (BTCUSD), and the dollar index (DXY) — 12 instruments across FX,
            metals, energy, crypto, and the dollar index. More instruments are
            on the roadmap.
          </>
        ),
      },
    ],
  },
  {
    title: "How it works",
    items: [
      {
        q: "Where does the news come from?",
        a: (
          <>
            Around 200 financial-news RSS/Atom feeds — tier-1 wires, central-bank
            press rooms, regulator releases, and reputable aggregators. We
            deduplicate the same story across wires into a single cluster, so
            you see it once rather than five times.
          </>
        ),
      },
      {
        q: "How fast is \"real-time\"? What's the data delay?",
        a: (
          <>
            Headlines typically appear within seconds of hitting our feeds. The
            per-instrument LLM analysis runs every ~5 minutes against the latest
            batch of stories — fast enough to react to breaking news, structured
            enough to avoid headline-driven noise.
          </>
        ),
      },
      {
        q: "How does the AI Copilot work?",
        a: (
          <>
            The Copilot is a sidebar chat where you can ask things like "why is
            gold moving?" or "what changed for EURUSD in the last hour?" It
            answers using the same clusters and analyses on your dashboard, with
            every claim linking back to its source article.
          </>
        ),
      },
      {
        q: "Does the analysis update with every new article?",
        a: (
          <>
            No — the analysis runs periodically (every ~5 minutes when there's
            new material). Brand-new clusters appear immediately in the "since
            last run" section of your dashboard, so you can always see what the
            model hasn't yet incorporated.
          </>
        ),
      },
    ],
  },
  {
    title: "Pricing & trial",
    items: [
      {
        q: "How does the 14-day trial work?",
        a: (
          <>
            Sign up, pick a plan, and you get full Pro or Analyst access for 14
            days. We bill you on day 15 unless you've cancelled. The trial is
            real-time — same data, same Copilot, no feature gating.
          </>
        ),
      },
      {
        q: "Do I need a credit card to start?",
        a: (
          <>
            Yes — Stripe collects your card up front to keep trial abuse
            manageable. You won't be charged until day 15, and we email you
            ahead of the first charge.
          </>
        ),
      },
      {
        q: "What's the difference between Pro and Analyst?",
        a: (
          <>
            Pro includes the trading dashboard and the AI Copilot. Analyst runs
            on our smarter, more capable model — better analyses, premium
            briefings, and a priority Copilot queue. See the{" "}
            <Link to="/pricing" className="underline hover:no-underline">
              pricing page
            </Link>{" "}
            for the full comparison.
          </>
        ),
      },
      {
        q: "Can I cancel anytime? Do I get a refund?",
        a: (
          <>
            Yes, cancel anytime from Account → Billing inside the app. Refunds
            follow the policy on our{" "}
            <Link to="/legal/refund" className="underline hover:no-underline">
              refund page
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    title: "Account & privacy",
    items: [
      {
        q: "What data do you store about me?",
        a: (
          <>
            Your email and a hashed password (via Supabase), your subscription
            state (via Stripe), and the materials you're following. With your
            consent, we use Sentry for error diagnostics — which captures your
            IP address and browser metadata but not session replays. See our{" "}
            <Link to="/legal/privacy" className="underline hover:no-underline">
              Privacy Policy
            </Link>{" "}
            for the full list.
          </>
        ),
      },
      {
        q: "Can I delete my account?",
        a: (
          <>
            Yes, anytime, from Account → Delete account inside the app. We
            delete your personal data within 30 days of the request, in line
            with our Privacy Policy.
          </>
        ),
      },
      {
        q: "How do I report a bug or request a feature?",
        a: (
          <>
            Use the{" "}
            <Link to="/contact-us" className="underline hover:no-underline">
              contact form
            </Link>{" "}
            or email{" "}
            <a
              href="mailto:support@tapelens.com"
              className="underline hover:no-underline"
            >
              support@tapelens.com
            </a>
            . We aim to respond within 5 business days.
          </>
        ),
      },
    ],
  },
];

function AccordionItem({ q, a }: QA) {
  return (
    <details className="group border-b border-border py-4 last:border-b-0">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium [&::-webkit-details-marker]:hidden">
        <span>{q}</span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
        {a}
      </div>
    </details>
  );
}

export function HelpPage() {
  return (
    <main className="container mx-auto max-w-3xl px-4 py-12">
      <Seo
        title="Help & FAQs — Tapelens"
        description="Answers to common questions about Tapelens: how the news analysis works, what's covered, how the trial works, and how to contact support."
        path="/help"
      />
      <header className="mb-10 space-y-2 border-b border-border pb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Help & FAQs</h1>
        <p className="text-sm text-muted-foreground">
          Still stuck? Reach us at{" "}
          <a
            href="mailto:support@tapelens.com"
            className="underline hover:no-underline"
          >
            support@tapelens.com
          </a>{" "}
          or via the{" "}
          <Link to="/contact-us" className="underline hover:no-underline">
            contact form
          </Link>
          .
        </p>
      </header>
      <div className="space-y-10">
        {SECTIONS.map((section) => (
          <section key={section.title}>
            <h2 className="mb-3 text-lg font-semibold">{section.title}</h2>
            <div className="rounded-lg border border-border bg-card px-5 py-1">
              {section.items.map((item) => (
                <AccordionItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}
